import { BLEND_MODES, Container, IDestroyOptions, Matrix, Rectangle, Texture } from "pixi.js"
import { SoftenedGraphics } from "./softened_graphics"
import { BakedGroups } from "./baked_groups"
import { shadowBakeResolution } from "./device_tier"
import { polygonsKey, ShadowPolygon } from "./building_shadow_geometry"
import { PlantSilhouette, ShadowStamp, stampQuad, stampsKey } from "./foliage_shadow_geometry"

/**
 * The world's sun shadows (walls, floors, roofs, trees, entity tiles: everything but bodies, which cast
 * their own). Shade MULTIPLIES what it falls on: `color` is what the sun's shadow multiplies the
 * ground's colours by -- a little under half, cooler in the blue (skylight, not soot) -- and `alpha`
 * how much of that applies (1 by full day; it follows the daylight down). Live tuning, like
 * window.occluderFade: window.buildingShadow.enabled / .alpha / .color / .indoors / .softness.
 * `indoors: false` (the default) keeps floors under a roof (or under the storey above) out of the sun's
 * shade -- at full strength it sank every interior into one flat dark, its furniture (drawn above the
 * shade) glowing on top -- while contact shade still grounds what stands there; `true` shades them. `contact` /
 * `contactColor` / `contactReach` / `contactSoftness` tune the contact shade at the foot of everything
 * standing on a floor, and `faces` the shade on every wall's south face (turned from the sun). Setting window.buildingShadow = false turns the shadows off. The sun is sun.ts's
 * WORLD_SUN.
 */
export const BUILDING_SHADOW = {
    enabled: true, alpha: 1, color: 0x727a91, indoors: false, softness: 1.25,
    /** Contact shade (castContactShadows): strength, what it multiplies by, its reach past a footprint, its blur. */
    contact: 1, contactColor: 0x84848f, contactReach: 4, contactSoftness: 4,
    /** What a wall's south face, turned from the sun, multiplies its colours by at full strength. */
    faces: 0xbdc4db,
    /** The walls' halo strength (silhouette_halo.ts HALO). */
    halo: 0.7,
    /** Sprites standing in shade take it (sprite_shade.ts); false leaves them lit. */
    sprites: true,
}

/**
 * A pitched roof lit by its slope (MapRenderingManager.shadeRoofSlopes), as multipliers: the front slope
 * darkens by `front` from under the ridge (`ridge` px into its row) to the eave; the eave's last
 * `eaveRows` px by `eave` more; `vergeWidth` px at each side by `verge`. Past the roof's edges it drops
 * shade into the slots round it (the drip): under the eave, and beside the verges.
 */
export const ROOF_SHADE = { front: 0.22, ridge: 18, eave: 0.74, eaveRows: 7, verge: 0.82, vergeWidth: 3,
    /** The drip shadow under an eave, onto whatever stands in the slot below it: `drip` at the eave, gone `dripDepth` px down; beside a verge, `side` gone in `sideWidth`. */
    drip: 0.45, dripDepth: 16, side: 0.62, sideWidth: 8 }

/** A wall face's tint at shadow strength `strength` (0: unshaded white, 1: the full `faces` shade). */
export const faceShadeTint = (faces: number, strength: number): number => {
    const s = Math.max(0, Math.min(1, strength))
    const mix = (shift: number) => Math.round(255 + (((faces >> shift) & 0xff) - 255) * s)
    return (mix(16) << 16) | (mix(8) << 8) | mix(0)
}

export const buildingShadowConfig = (): typeof BUILDING_SHADOW => {
    if (typeof window === "undefined") return BUILDING_SHADOW
    const w = window as any
    if (w.buildingShadow === undefined) w.buildingShadow = BUILDING_SHADOW
    const live = w.buildingShadow
    if (live === false) return { ...BUILDING_SHADOW, enabled: false }
    return live && typeof live === "object" ? live : BUILDING_SHADOW
}

/** Above every ground tile layer (zIndex 0), below every sorted sprite and body (100 and up). */
export const BUILDING_SHADOW_Z_INDEX = 1

/** How strongly shadows show at a room's brightness: full by day, gone by night and underground. */
export const daylightFactor = (brightness: number | undefined): number =>
    brightness === undefined ? 1 : Math.min(1, Math.max(0, (brightness - 0.4) / 0.6))

/**
 * Polygons that overlap or nearly touch, grouped, so each building bakes into its own small texture
 * rather than one texture spanning every building in the room.
 */
export const groupPolygons = (polygons: readonly ShadowPolygon[], pad = 8, bucket = 64): ShadowPolygon[][] => {
    const parent = polygons.map((_, i) => i)
    const find = (i: number): number => (parent[i] === i ? i : (parent[i] = find(parent[i])))
    const owner = new Map<string, number>()
    polygons.forEach((polygon, i) => {
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
        for (let k = 0; k < polygon.length; k += 2) {
            minX = Math.min(minX, polygon[k]); maxX = Math.max(maxX, polygon[k])
            minY = Math.min(minY, polygon[k + 1]); maxY = Math.max(maxY, polygon[k + 1])
        }
        for (let bx = Math.floor((minX - pad) / bucket); bx <= Math.floor((maxX + pad) / bucket); bx++) {
            for (let by = Math.floor((minY - pad) / bucket); by <= Math.floor((maxY + pad) / bucket); by++) {
                const key = `${bx},${by}`
                const other = owner.get(key)
                if (other === undefined) owner.set(key, i)
                else parent[find(i)] = find(other)
            }
        }
    })
    const groups = new Map<number, ShadowPolygon[]>()
    polygons.forEach((polygon, i) => {
        const root = find(i)
        const group = groups.get(root)
        if (group) group.push(polygon)
        else groups.set(root, [polygon])
    })
    return [...groups.values()]
}

/** A plant's silhouette as a black picture, one texture per plant and strength, and a view of it per run of rows. */
const silhouettes = new WeakMap<PlantSilhouette, Map<number, { texture: Texture; rows: Map<string, Texture> }>>()
const stampTexture = ({ art, y, height, strength = 1 }: ShadowStamp): Texture => {
    let byStrength = silhouettes.get(art)
    if (!byStrength) silhouettes.set(art, byStrength = new Map())
    let made = byStrength.get(strength)
    if (!made) {
        // White where the art is drawn (premultiplied, scaled by its strength): the stamp is tinted the shadow's colour.
        const rgba = new Uint8Array(art.width * art.height * 4)
        for (let i = 0; i < art.alpha.length; i++) rgba.fill(Math.round(art.alpha[i] * strength), i * 4, i * 4 + 4)
        made = { texture: Texture.fromBuffer(rgba, art.width, art.height), rows: new Map() }
        byStrength.set(strength, made)
    }
    const key = `${y}:${height}`
    let rows = made.rows.get(key)
    if (!rows) made.rows.set(key, rows = new Texture(made.texture.baseTexture, new Rectangle(0, y, art.width, height)))
    return rows
}

/**
 * One storey's sun shadows: every polygon filled solid and every stamp (a plant's silhouette drawn
 * black through its map, foliage_shadow_geometry.ts) drawn with them, softened and baked once per
 * change (SoftenedGraphics), and shown through the layer's alpha so overlapping shadows never darken
 * twice. No live filter, mask or extra pass per frame.
 *
 * A group whose shape is unchanged keeps its bake: a wall built in a clearing re-bakes only its own
 * building, never the forest's shadow beside it (foliage_shadow.ts), which grows as wide as the room.
 */
export class BuildingShadowLayer extends Container {
    private baked = new BakedGroups<{ key: string; graphics: SoftenedGraphics }>(this)
    /** How many groups the last draw baked afresh (the rest kept their bake). */
    lastBaked = 0

    /**
     * `resolution`: the bakes' pixels per world pixel; by default the device's (device_tier.ts): full,
     * or half on a budget machine. A forest's shadow is one group as wide as the room, so at full
     * resolution one room's shade comes to tens of MB of textures (measured 34MB, 8MB at half).
     */
    constructor(private readonly resolution?: number) {
        super()
        this.zIndex = BUILDING_SHADOW_Z_INDEX
        this.interactive = false
        this.interactiveChildren = false
    }

    /**
     * `stampArea(x0, y0, x1, y1)`: where stamps may land within a box (building_shadow_geometry.ts
     * sunReceivingRects), for a storey whose floor ends or is roofed over; undefined for anywhere.
     */
    draw(polygons: readonly ShadowPolygon[], softness: number, stamps: readonly ShadowStamp[] = [], color = 0x000000,
        stampArea?: (x0: number, y0: number, x1: number, y1: number) => { x0: number; y0: number; x1: number; y1: number }[]): void {
        const previous = new Map(this.baked.groups.map(group => [group.key, group]))
        const next: { key: string; graphics: SoftenedGraphics }[] = []
        const fresh: SoftenedGraphics[] = []
        // A stamp is grouped by the quadrilateral it lands on.
        const quads = new Map(stamps.map(stamp => [stampQuad(stamp), stamp]))
        const resolution = this.resolution ?? shadowBakeResolution()
        groupPolygons([...polygons, ...quads.keys()]).forEach(members => {
            const group = members.filter(polygon => !quads.has(polygon))
            const groupStamps = members.filter(polygon => quads.has(polygon)).map(quad => quads.get(quad))
            let area: { x: number; y: number; width: number; height: number }[] | undefined
            if (stampArea && groupStamps.length) {
                const xs = groupStamps.flatMap(stamp => stampQuad(stamp).filter((_, i) => i % 2 === 0))
                const ys = groupStamps.flatMap(stamp => stampQuad(stamp).filter((_, i) => i % 2 === 1))
                area = stampArea(Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys))
                    .map(({ x0, y0, x1, y1 }) => ({ x: x0, y: y0, width: x1 - x0, height: y1 - y0 }))
            }
            const areaKey = area ? polygonsKey(area.map(({ x, y, width, height }) => [x, y, width, height])) : "-"
            const key = `${softness}|${color}|${resolution}|${polygonsKey(group)}|${stampsKey(groupStamps)}|${areaKey}`
            const kept = previous.get(key)
            if (kept) {
                previous.delete(key)
                next.push(kept)
                return
            }
            const graphics = new SoftenedGraphics(softness, 2, BLEND_MODES.MULTIPLY, resolution)
            if (group.length) {
                graphics.beginFill(color, 1)
                group.forEach(polygon => graphics.drawPolygon(polygon))
                graphics.endFill()
            }
            groupStamps.forEach(stamp => graphics.stamp(stampTexture(stamp), new Matrix(...stamp.matrix), color))
            graphics.maskStamps(area)
            next.push({ key, graphics })
            fresh.push(graphics)
        })
        this.lastBaked = fresh.length
        this.baked.replace(next, fresh)
    }

    get groupCount(): number { return this.baked.groups.length }

    destroy(options?: IDestroyOptions | boolean): void {
        this.baked.clear()
        super.destroy(options)
    }
}
